import { CLIENT_STATUSES } from '@hirebotiq-ops/core';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { StaffClientCreateForm } from '../components/forms/StaffClientCreateForm.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/clients/')({
  component: AdminClients,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type StatusFilter = 'all' | (typeof CLIENT_STATUSES)[number];

function AdminClients() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [creating, setCreating] = useState(false);
  const list = trpc.clients.list.useQuery({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    limit: 200,
  });

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold">Kunden</h1>
          <Button
            variant={creating ? 'ghost' : 'default'}
            size="sm"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? 'Schließen' : '+ Firma anlegen'}
          </Button>
        </header>

        {creating && (
          <Card>
            <CardHeader>
              <CardTitle>Neue Firma anlegen</CardTitle>
              <CardDescription>
                Entleiher ohne Login-User vorab anlegen (auditiert). Weitere Stammdaten
                ergänzt der Kunde später selbst.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StaffClientCreateForm onCreated={() => setCreating(false)} />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Input
              placeholder="Suche nach Firmenname oder USt-ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {CLIENT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Firma</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stadt</TableHead>
                  <TableHead>USt-ID</TableHead>
                  <TableHead>Zuletzt geändert</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Lade …
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Keine Treffer.
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Link
                        to="/admin/clients/$id"
                        params={{ id: c.id }}
                        className="hover:underline"
                      >
                        {c.companyName ?? '(unbenannt)'}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={c.status} />
                    </TableCell>
                    <TableCell>{c.registeredAddressCity ?? '—'}</TableCell>
                    <TableCell>{c.vatId ?? '—'}</TableCell>
                    <TableCell className="text-xs text-[var(--muted-foreground)]">
                      {new Date(c.updatedAt).toLocaleString('de-DE')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
